import { useQuery } from '@tanstack/react-query';
import { getLocalPrinters } from '@/lib/zebra';
import { configurationQueries } from '../services/configurations.queries';

/**
 * Returns app settings from the server cache.
 * Safe to call from any client component — TanStack Query deduplicates.
 */
export function useAppSettings() {
  const { data } = useQuery(configurationQueries.all());
  return (data as any)?.data ?? data ?? {};
}

/**
 * Convenience hook: is the QA2 OCR stage enabled?
 * When disabled, qa1_ocr advances directly to qa3_ocr.
 */
export function useQa2OcrStageEnabled(): boolean {
  const settings = useAppSettings();
  return settings.enable_qa2_ocr ?? false;
}

/**
 * Convenience hook: is the hardware document scanner the active capture mode
 * on the photo-scanning page? When true, the Dynamsoft Web TWAIN scanner panel
 * and per-page hardware re-scan action are shown; when false, manual file
 * upload is used instead. Defaults to true (scanner-first deployments).
 */
export function useHardwareScannerEnabled(): boolean {
  const settings = useAppSettings();
  return settings.use_hardware_scanner ?? true;
}

/**
 * Convenience hook: are in-app notifications (bell + drawer) enabled?
 * Defaults to true.
 */
export function useNotificationsEnabled(): boolean {
  const settings = useAppSettings();
  return settings.notifications_enabled ?? true;
}

/**
 * Convenience hook: are OCR highlights (bounding boxes) enabled?
 */
export function useOcrHighlightsEnabled(): boolean {
  const settings = useAppSettings();
  return settings.ocr_highlights_enabled ?? true;
}

/** Fallback when the setting row is missing (fresh DB, cache miss). */
export const DEFAULT_OCR_HIGHLIGHT_COLOR = '#8b5cf6';

/**
 * Convenience hook: default highlight color for extracted OCR fields that
 * carry no per-field color on their template. Applies to the viewer overlay,
 * the field cards, and the "download with highlights" PDF export.
 */
export function useOcrDefaultHighlightColor(): string {
  const settings = useAppSettings();
  const value = settings.ocr_default_highlight_color;

  return typeof value === 'string' && /^#[0-9A-Fa-f]{6}$/.test(value)
    ? value
    : DEFAULT_OCR_HIGHLIGHT_COLOR;
}

/**
 * Convenience hook: global cap on PDF files per upload selection.
 * 0 = unlimited (default). Compose with enforcePdfFileLimit() from
 * '@/lib/pdf-upload-limit' on any surface that accepts multiple PDF files.
 */
export function useMaxPdfUploadFiles(): number {
  const settings = useAppSettings();
  const raw = Number(settings.max_pdf_upload_files);
  return Number.isFinite(raw) && raw >= 0 ? Math.floor(raw) : 0;
}

/**
 * Convenience hook: system-wide ceiling on total uploaded/scanned pages at the
 * Scan Upload stage. 0 = unlimited (default).
 */
export function useMaxScanPages(): number {
  const settings = useAppSettings();
  const raw = Number(settings.max_scan_pages);
  return Number.isFinite(raw) && raw >= 0 ? Math.floor(raw) : 0;
}

/**
 * Live snapshot of the system-wide scan-upload page quota (used / limit).
 * Computed server-side from the database, so it reflects real usage.
 */
export function useScanPageQuota() {
  return useQuery(configurationQueries.scanPageQuota());
}

/**
 * Convenience hook: label print physical dimensions (mm) and DPI.
 */
export function useLabelPrintConfig() {
  const settings = useAppSettings();
  return {
    labelWidthMm: (settings.label_width_mm as number) ?? 12.7,
    labelHeightMm: (settings.label_height_mm as number) ?? 12.7,
    gapMm: (settings.label_gap_mm as number) ?? 3.175,
    labelDpi: (settings.label_dpi as number) ?? 203,
    // Per-side inner gaps between the QR symbol and the text rows above
    // (header — container number) and below (footer — page number).
    // 0.5 mm default matches the historical computed margin (~4 % of cell
    // dim at common sticker sizes); operator can tune each side from the
    // UI independently.
    qrHeaderGapMm: (settings.qr_header_gap_mm as number) ?? 0.5,
    qrFooterGapMm: (settings.qr_footer_gap_mm as number) ?? 0.5,
    // Explicit text height (mm) for the header and footer rows. 0 = auto
    // (the ZPL solver picks the largest font that fits the QR width and
    // caps at 12% of label height). Used by Zebra ZPL, the in-dialog
    // preview, and the browser-print fallback so all three stay in sync.
    qrHeaderFontMm: Math.max(0, (settings.qr_header_font_mm as number) ?? 0),
    qrFooterFontMm: Math.max(0, (settings.qr_footer_font_mm as number) ?? 0)
  };
}

export type PrintMode = 'sticker' | 'grid' | 'continuous';

/**
 * Convenience hook: grid-print configuration. When `mode === 'grid'`, the
 * indexing print flow lays out cols × rows cells per sheet (one Zebra feed
 * per sheet) instead of one feed per label.
 *
 * Defaults match the migration so behaviour is stable when keys are missing
 * from the API response (e.g. on stale clients post-migration).
 */
export function useGridPrintConfig() {
  const settings = useAppSettings();
  const rawMode = settings.print_mode as string | undefined;
  const mode: PrintMode =
    rawMode === 'grid'
      ? 'grid'
      : rawMode === 'continuous'
        ? 'continuous'
        : 'sticker';
  return {
    mode,
    paperWidthMm: (settings.paper_width_mm as number) ?? 100,
    paperHeightMm: (settings.paper_height_mm as number) ?? 75,
    cols: Math.max(1, Math.floor((settings.grid_cols as number) ?? 3)),
    rows: Math.max(1, Math.floor((settings.grid_rows as number) ?? 3)),
    marginMm: (settings.grid_margin_mm as number) ?? 2
  };
}

/**
 * Convenience hook: continuous-roll multi-column configuration.
 * Used when the printer is fed a continuous die-cut roll with multiple
 * label columns across its width. Each ZPL feed paints `columns` labels
 * in one row; the printer's gap sensor advances one label-pitch per feed.
 */
export function useContinuousPrintConfig() {
  const settings = useAppSettings();
  return {
    columns: Math.max(
      1,
      Math.floor((settings.continuous_columns as number) ?? 1)
    ),
    edgeMarginMm: Math.max(
      0,
      (settings.continuous_edge_margin_mm as number) ?? 0
    ),
    // Operator's measured physical roll width (mm). Used by the
    // configuration UI to flag mismatch between the configured paper
    // width and the actual die-cut roll. 0 = unset (no warning shown).
    physicalRollWidthMm: Math.max(
      0,
      (settings.continuous_physical_roll_width_mm as number) ?? 0
    )
  };
}

/**
 * Convenience hook: UID of the Zebra printer chosen as default in
 * Configurations. `null` when unset — caller should fall back to the
 * browser print dialog.
 */
export function useDefaultZebraPrinterUid(): string | null {
  const settings = useAppSettings();
  const uid = settings.default_zebra_printer_uid as string | null | undefined;
  return uid && uid.length > 0 ? uid : null;
}

/**
 * Convenience hook: best-effort active Zebra printer UID.
 *
 * 1. If an explicit `default_zebra_printer_uid` is saved in settings, use it.
 * 2. Otherwise auto-discover via BrowserPrint and return the first Zebra
 *    found on this workstation (cached for 60s per session).
 * 3. Returns `null` when no Zebra is reachable — callers fall back to
 *    `window.print()`.
 *
 * This hook lets the print flow work end-to-end without any "default
 * printer" UI — plug in a Zebra, hit print, it picks the connected one.
 */
export function useActiveZebraPrinterUid(): string | null {
  const savedUid = useDefaultZebraPrinterUid();

  const { data: discoveredUid } = useQuery({
    queryKey: ['zebra-auto-printer'],
    queryFn: async () => {
      try {
        const printers = await getLocalPrinters();
        return printers[0]?.uid ?? null;
      } catch {
        return null;
      }
    },
    enabled: !savedUid,
    staleTime: 60_000,
    retry: false
  });

  if (savedUid) return savedUid;
  return discoveredUid ?? null;
}

/**
 * Convenience hook: list of per-printer profiles (model pattern, DPI,
 * label dimensions). Used at print time to look up the right ZPL
 * options based on the connected printer's name.
 */
/**
 * Convenience hook: lab-association mode for dynamic forms.
 * `single` (default) — one lab per form; `multi` — multiple labs per form.
 */
export function useDynamicFormLabMode(): 'single' | 'multi' {
  const settings = useAppSettings();
  const raw = settings.dynamic_form_lab_mode as string | undefined;
  return raw === 'multi' ? 'multi' : 'single';
}

export function useZebraPrinterProfiles() {
  const settings = useAppSettings();
  const raw = settings.zebra_printer_profiles;
  return Array.isArray(raw)
    ? (raw as Array<{
        name_pattern: string;
        dpi: number;
        label_width_mm: number;
        label_height_mm: number;
        gap_mm: number;
      }>)
    : [];
}
